import type { FC, ReactElement } from "react"

import { motion, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import { DateTime } from "luxon"

import { Link } from "@/3514/components/shared/link/link.component"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyColor,
    ETypographySize,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { type TOrientation, type TScreenBreakpoints, useOrientation, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

const classes = {
    footer: ({ isMobileLandscape, mode }: { isMobileLandscape: boolean; mode: "onboarding" | "auth" }): string =>
        buildCssClassName("flex items-center gap-x-[10px]", isMobileLandscape && "py-[20px]", {
            "mt-[50px]": eq(mode, "onboarding"),
            "mt-auto": eq(mode, "auth")
        }),
    footerCopyright: (): string => "mb-0",
    footerDivider: (): string => "w-[0.5px] h-[8px] bg-gray-200"
}

type TProps = { mode?: "onboarding" | "auth" }

const ParticipantFooter: FC<TProps> = ({ mode = "auth" }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const { isLandscape }: TOrientation = useOrientation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isReducedMotion: boolean = useReducedMotion()

    const isMobileLandscape: boolean = isLandscape && (breakpoints.xs || breakpoints.sm)

    return (
        <motion.footer
            className={classes.footer({ isMobileLandscape, mode })}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: isReducedMotion ? 0 : 0.35, ease: "easeOut" }}
        >
            <Typography
                text={t("auth.footer.copyright", { year: DateTime.now().year })}
                color={ETypographyColor.Dark}
                size={ETypographySize.Tiny}
                className={classes.footerCopyright()}
            />

            <div className={classes.footerDivider()} />

            <Link
                to="/privacy"
                textSize={ETypographySize.Tiny}
                textColor={ETypographyColor.Blue200}
                textWeight={ETypographyWeight.Default}
            >
                {t("auth.footer.privacy")}
            </Link>
        </motion.footer>
    )
}

ParticipantFooter.displayName = "ParticipantFooter"

export { ParticipantFooter }
